import { DailyAreaTrendChart } from '@/pages/dashboard/components/DailyAreaTrendChart'
import { MenuLinks } from '@/constants/menu'
import { DashboardChartEmpty } from '@/pages/dashboard/components/DashboardChartEmpty'
import { DashboardChartHost } from '@/pages/dashboard/components/DashboardChartHost'
import { DashboardDateFilter } from '@/pages/dashboard/components/DashboardDateFilter'
import { DashboardKpiGrid } from '@/pages/dashboard/components/DashboardKpiGrid'
import { DashboardSection } from '@/pages/dashboard/components/DashboardSection'
import { chartPanel, chartTheme, STATUS_COLORS, baseChartOptions } from '@/pages/dashboard/components/dashboardTheme'
import { useDateRangeFilter } from '@/pages/dashboard/hooks/useDateRangeFilter'
import { formatRelativeTime } from '@/pages/dashboard/utils/formatRelativeTime'
import moment from 'moment'
import { buildDailyBuckets, filterByDateRange } from '@/pages/dashboard/utils/dateRangeUtils'
import { licenseAdminApi, type LicenseAuditLogResponse } from '@/services/licenseAdminApi'
import { LicenseStatusTypes, LicenseTypeTypes, type TenantLicenseListResponse } from '@/services/licenseTypes'
import type { ApexOptions } from 'apexcharts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'

function isActivateEvent(row: LicenseAuditLogResponse) {
	return row.eventType?.toLowerCase() === 'activate'
}

function licenseStatusBadge(status: LicenseStatusTypes) {
	switch (status) {
		case LicenseStatusTypes.Active:
			return 'bg-success/15 text-success'
		case LicenseStatusTypes.Expired:
			return 'bg-destructive/15 text-destructive'
		case LicenseStatusTypes.PendingIssue:
			return 'bg-warning/15 text-warning'
		case LicenseStatusTypes.Revoked:
			return 'bg-destructive/15 text-destructive'
		default:
			return 'bg-muted/30 text-muted-foreground'
	}
}

function licenseStatusLabel(status: LicenseStatusTypes) {
	switch (status) {
		case LicenseStatusTypes.Active:
			return 'Active'
		case LicenseStatusTypes.Expired:
			return 'Expired'
		case LicenseStatusTypes.PendingIssue:
			return 'Pending'
		case LicenseStatusTypes.Inactive:
			return 'Inactive'
		case LicenseStatusTypes.Revoked:
			return 'Revoked'
		default:
			return 'Unknown'
	}
}

export function LicenseDashboardSection() {
	const dateFilter = useDateRangeFilter('30')
	const [loading, setLoading] = useState(true)
	const [auditRows, setAuditRows] = useState<LicenseAuditLogResponse[]>([])
	const [licenseRows, setLicenseRows] = useState<TenantLicenseListResponse[]>([])
	const [statusCounts, setStatusCounts] = useState<Record<string, number>>({})

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const statusList = [
				LicenseStatusTypes.Active,
				LicenseStatusTypes.PendingIssue,
				LicenseStatusTypes.Inactive,
				LicenseStatusTypes.Revoked,
				LicenseStatusTypes.Expired,
			]
			const [audit, licenses, ...statusPages] = await Promise.all([
				licenseAdminApi.searchAudit({ pageNumber: 1, pageSize: 500 }),
				licenseAdminApi.search({ pageNumber: 1, pageSize: 500 }),
				...statusList.map((licenseStatus) =>
					licenseAdminApi.search({ pageNumber: 1, pageSize: 1, licenseStatus })
				),
			])
			setAuditRows(audit.data ?? [])
			setLicenseRows(licenses.data ?? [])
			setStatusCounts(
				Object.fromEntries(statusList.map((status, index) => [status, statusPages[index]?.totalCount ?? 0]))
			)
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		load()
	}, [load])

	const { dateFrom, dateTo } = dateFilter.appliedRange
	const filteredAudit = useMemo(() => filterByDateRange(auditRows, dateFrom, dateTo), [auditRows, dateFrom, dateTo])
	const filteredLicenses = useMemo(
		() =>
			filterByDateRange(licenseRows, dateFrom, dateTo).sort(
				(a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
			),
		[licenseRows, dateFrom, dateTo]
	)
	const activationSeries = useMemo(
		() => buildDailyBuckets(auditRows, dateFrom, dateTo, () => 1, isActivateEvent),
		[auditRows, dateFrom, dateTo]
	)
	const paidLicensesInPeriod = useMemo(
		() => filteredLicenses.filter((l) => l.licenseType === LicenseTypeTypes.Paid),
		[filteredLicenses]
	)
	const trialLicensesInPeriod = useMemo(
		() => filteredLicenses.filter((l) => l.licenseType === LicenseTypeTypes.Trial),
		[filteredLicenses]
	)
	const expiredLicensesInPeriod = useMemo(() => {
		const start = moment(dateFrom).startOf('day')
		const end = moment(dateTo).endOf('day')
		return licenseRows
			.filter(
				(l) =>
					l.licenseType === LicenseTypeTypes.Paid &&
					l.licenseStatus === LicenseStatusTypes.Expired &&
					moment(l.expiresOn).isValid() &&
					!moment(l.expiresOn).isBefore(start) &&
					!moment(l.expiresOn).isAfter(end)
			)
			.sort((a, b) => new Date(b.expiresOn).getTime() - new Date(a.expiresOn).getTime())
	}, [licenseRows, dateFrom, dateTo])
	const activationCount = useMemo(() => filteredAudit.filter(isActivateEvent).length, [filteredAudit])

	const typeDonut = useMemo(() => {
		const trial = trialLicensesInPeriod.length
		const paid = paidLicensesInPeriod.length
		return [
			{ label: 'Trial', value: trial, color: STATUS_COLORS.Trial },
			{ label: 'Paid', value: paid, color: STATUS_COLORS.Paid },
		].filter((s) => s.value > 0)
	}, [trialLicensesInPeriod, paidLicensesInPeriod])

	const statusDonut = useMemo(() => {
		const counts = {
			Active: statusCounts[LicenseStatusTypes.Active] ?? 0,
			Pending: statusCounts[LicenseStatusTypes.PendingIssue] ?? 0,
			Inactive: statusCounts[LicenseStatusTypes.Inactive] ?? 0,
			Revoked: statusCounts[LicenseStatusTypes.Revoked] ?? 0,
			Expired: statusCounts[LicenseStatusTypes.Expired] ?? 0,
		}
		const colors: Record<string, string> = {
			Active: STATUS_COLORS.Active,
			Pending: STATUS_COLORS.Pending,
			Inactive: STATUS_COLORS.Inactive,
			Revoked: STATUS_COLORS.Failed,
			Expired: STATUS_COLORS.Expired,
		}
		return Object.entries(counts)
			.filter(([, value]) => value > 0)
			.map(([label, value]) => ({ label, value, color: colors[label] ?? STATUS_COLORS.Inactive }))
	}, [statusCounts])

	const conversionDenom = paidLicensesInPeriod.length + trialLicensesInPeriod.length
	const conversionPct =
		conversionDenom > 0 ? Math.round((paidLicensesInPeriod.length / conversionDenom) * 100) : 0

	const kpis = [
		{
			label: 'Issued licenses',
			value: paidLicensesInPeriod.length.toLocaleString(),
			hint: `${paidLicensesInPeriod.filter((l) => l.licenseStatus === LicenseStatusTypes.Active).length} active paid · trials excluded`,
			icon: 'ri-key-2-line',
			accentClass: 'text-primary',
		},
		{
			label: 'Activations',
			value: activationCount.toLocaleString(),
			hint: 'License activation events',
			icon: 'ri-flashlight-line',
			accentClass: 'text-[#1ed27d]',
		},
		{
			label: 'Trial → paid',
			value: conversionDenom > 0 ? `${conversionPct}%` : '—',
			hint: `${trialLicensesInPeriod.length} trials · ${paidLicensesInPeriod.length} paid in period`,
			icon: 'ri-arrow-right-up-line',
			accentClass: 'text-info',
		},
		{
			label: 'Expired licenses',
			value: expiredLicensesInPeriod.length.toLocaleString(),
			hint: 'Paid licenses expired in selected period',
			icon: 'ri-time-line',
			accentClass: 'text-destructive',
		},
	]

	const donutOptions = (labels: string[], colors: string[], total: number): ApexOptions => ({
		chart: { ...baseChartOptions, type: 'donut' },
		theme: { mode: 'dark' },
		labels,
		colors,
		stroke: { width: 0 },
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px', labels: { colors: chartTheme.foreColor } },
		plotOptions: {
			pie: {
				donut: {
					size: '70%',
					labels: {
						show: true,
						total: {
							show: true,
							label: 'Total',
							color: chartTheme.foreColor,
							formatter: () => total.toLocaleString(),
						},
					},
				},
			},
		},
		tooltip: { theme: 'dark' },
	})

	const recentlyIssuedLicenses = paidLicensesInPeriod
		.filter((l) => l.licenseStatus !== LicenseStatusTypes.Expired)
		.slice(0, 6)
	const recentAudit = [...filteredAudit]
		.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
		.slice(0, 6)
	const statusDonutTotal = statusDonut.reduce((sum, item) => sum + item.value, 0)

	return (
		<DashboardSection
			title="Licenses & activations"
			subtitle={dateFilter.rangeLabel}
			icon="ri-key-2-line"
			analyticsLink={MenuLinks.SubscriptionSales}
			filter={
				<DashboardDateFilter
					preset={dateFilter.preset}
					dateFrom={dateFilter.dateFrom}
					dateTo={dateFilter.dateTo}
					onPresetClick={dateFilter.onPresetClick}
					onDateFromChange={(v) => {
						dateFilter.setDateFrom(v)
					}}
					onDateToChange={(v) => {
						dateFilter.setDateTo(v)
					}}
					onApply={dateFilter.onApplyRange}
				/>
			}
		>
			<DashboardKpiGrid items={kpis} />

			<div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(260px,1fr)_minmax(260px,1fr)]">
				<div className={chartPanel}>
					<div className="mb-3 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-foreground">Activation trend</h3>
						<span className="text-xs text-muted-foreground">Daily</span>
					</div>
					{loading ? (
						<div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">Loading chart…</div>
					) : (
						<DailyAreaTrendChart
							labels={activationSeries.labels}
							buckets={activationSeries.buckets}
							dateFrom={dateFrom}
							seriesName="Activations"
							color="#6366f1"
							valueFormatter={(v) => `${Math.round(v)} activation${Math.round(v) === 1 ? '' : 's'}`}
							emptyMessage="No activations in this date range."
						/>
					)}
				</div>

				<div className={chartPanel}>
					<h3 className="mb-3 text-sm font-semibold text-foreground">License type</h3>
					{loading ? (
						<div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">Loading…</div>
					) : typeDonut.length > 0 ? (
						<DashboardChartHost>
							<ReactApexChart
								options={donutOptions(
									typeDonut.map((s) => s.label),
									typeDonut.map((s) => s.color),
									paidLicensesInPeriod.length + trialLicensesInPeriod.length
								)}
								series={typeDonut.map((s) => s.value)}
								type="donut"
								height={280}
								width="100%"
							/>
						</DashboardChartHost>
					) : (
						<DashboardChartEmpty message="No licenses issued in this period." />
					)}
				</div>

				<div className={chartPanel}>
					<h3 className="mb-3 text-sm font-semibold text-foreground">Status mix</h3>
					{loading ? (
						<div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">Loading…</div>
					) : statusDonut.length > 0 ? (
						<DashboardChartHost>
							<ReactApexChart
								options={donutOptions(
									statusDonut.map((s) => s.label),
									statusDonut.map((s) => s.color),
									statusDonutTotal
								)}
								series={statusDonut.map((s) => s.value)}
								type="donut"
								height={280}
								width="100%"
							/>
						</DashboardChartHost>
					) : (
						<DashboardChartEmpty message="No status data for this period." />
					)}
				</div>
			</div>

			<div className="mt-5 grid gap-5 lg:grid-cols-2">
				<div className={chartPanel}>
					<h3 className="mb-4 text-sm font-semibold text-foreground">Expired licenses</h3>
					{expiredLicensesInPeriod.length === 0 ? (
						<p className="text-sm text-muted-foreground">No paid licenses expired in selected range.</p>
					) : (
						<ul className="space-y-3">
							{expiredLicensesInPeriod.slice(0, 6).map((row) => {
								const name = row.adminDisplayName || row.adminEmail || 'Unknown'
								return (
									<li key={row.id} className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-background/40 px-3 py-2.5">
										<div className="min-w-0">
											<div className="truncate text-sm font-semibold text-foreground">{name}</div>
												<div className="truncate text-xs text-muted-foreground">
													{row.tenantName || row.planName} · started {moment(row.startedOn).format('MMM D, YYYY')} · expired {moment(row.expiresOn).format('MMM D, YYYY')}
												</div>
										</div>
										<span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase ${licenseStatusBadge(row.licenseStatus)}`}>
											Expired
										</span>
									</li>
								)
							})}
						</ul>
					)}

					{recentlyIssuedLicenses.length > 0 && (
						<div className="mt-6 border-t border-border/60 pt-5">
							<h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recently issued</h4>
							<ul className="space-y-3">
								{recentlyIssuedLicenses.map((row) => {
									const name = row.adminDisplayName || row.adminEmail || 'Unknown'
									return (
										<li key={row.id} className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-background/40 px-3 py-2.5">
											<div className="min-w-0">
												<div className="truncate text-sm font-semibold text-foreground">{name}</div>
												<div className="truncate text-xs text-muted-foreground">
													{row.tenantName || row.planName} · starts {moment(row.startedOn).format('MMM D, YYYY')}
												</div>
											</div>
											<span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase ${licenseStatusBadge(row.licenseStatus)}`}>
												{licenseStatusLabel(row.licenseStatus)}
											</span>
										</li>
									)
								})}
							</ul>
						</div>
					)}
				</div>

				<div className={chartPanel}>
					<h3 className="mb-4 text-sm font-semibold text-foreground">Recent activity</h3>
					{recentAudit.length === 0 ? (
						<p className="text-sm text-muted-foreground">No audit events in selected range.</p>
					) : (
						<ul className="space-y-3">
							{recentAudit.map((row) => (
								<li key={row.id} className="flex items-start justify-between gap-3 rounded-lg border border-border/50 bg-background/40 px-3 py-2.5">
									<span className="text-sm text-foreground/90">{row.summary || row.eventType}</span>
									<span className="shrink-0 text-xs text-muted-foreground">{formatRelativeTime(row.createdOn)}</span>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</DashboardSection>
	)
}
